import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queries';
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import {
    fetchPrescriptionSpecialTemplatesApi,
    addPrescriptionSpecialTemplateApi,
    updatePrescriptionSpecialTemplateApi,
    deletePrescriptionSpecialTemplateApi,
    updatePrescriptionSpecialTemplateOrderApi
} from "../services/apiService";
import cancelIcon from "../assets/images/cancel-btn.svg";
import editIcon from "../assets/images/edit-icon.svg";
import deleteIcon from "../assets/images/delete-icon.svg";

const SpecialTemplatesManagement = ({ refreshKey, userData, allPatientDetails, onOpenDownloadModal }) => {
    const queryClient = useQueryClient();
    const [specialTemplates, setSpecialTemplates] = useState([]);
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [templateContent, setTemplateContent] = useState("");
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const categories = [
        {
            id: 1,
            title: "Imaging Request",
            type: 'imagerie'
        },
        {
            id: 2,
            title: "Medical Certificate",
            type: 'certificat'
        },
        {
            id: 3,
            title: "Referral Letter",
            type: 'orientation'
        },
        {
            id: 4,
            title: "Laboratory Tests Request",
            type: 'analyse'
        },
        {
            id: 5,
            title: "Specialty",
            type: 'specialite'
        }
    ];

    const joditConfig = useMemo(() => {
        const isAnalyse = currentCategory?.type === 'analyse';

        return {
            readonly: false,
            placeholder: "Enter template details...",
            toolbarAdaptive: false,
            buttons: [
                'bold', 'italic', 'underline', 'strikethrough', '|',
                'ul', 'ol', '|',
                'outdent', 'indent', '|',
                'font', 'fontsize', 'brush', 'paragraph', '|',
                'image', 'video', 'table', 'link', '|',
                'align', 'undo', 'redo', '|',
                'hr', 'eraser', 'copyformat', '|',
                'symbol', 'fullsize', 'print', 'about', '|'
            ],
            extraButtons: [
                {
                    name: 'var',
                    tooltip: 'Insert Variable',
                    popup: (editor, current, self, close) => {
                        const div = editor.create.div();
                        const patientName = allPatientDetails?.data?.[0]?.first_name && allPatientDetails?.data?.[0]?.last_name
                            ? `${allPatientDetails.data[0].first_name} ${allPatientDetails.data[0].last_name}`
                            : 'N/A';
                        const patientDOB = allPatientDetails?.data?.[0]?.dob
                            ? new Date(allPatientDetails.data[0].dob).toLocaleDateString()
                            : 'N/A';
                        const patientAge = allPatientDetails?.data?.[0]?.age || 'N/A';
                        const patientGender = allPatientDetails?.data?.[0]?.gender || 'N/A';
                        const doctorName = userData?.users?.name || 'N/A';
                        const currentDate = new Date().toLocaleDateString();

                        const variables = [
                            { label: "Date", value: "[DATE]" },
                            { label: "Date de naissance", value: "[DATE_NAISSANCE]" },
                            { label: "Âge", value: "[AGE] ans" },
                            { label: "Patient", value: "[PATIENT]" },
                            { label: "Sexe", value: "[SEXE]" },
                            { label: "Docteur", value: "[DOCTEUR]" },
                        ];

                        div.innerHTML = `
                    <style>
                        .var-popup-item {
                            padding: 8px 12px;
                            cursor: pointer;
                            white-space: nowrap;
                        }
                        .var-popup-item:hover {
                            background-color: #f0f0f0;
                        }
                    </style>
                    ${variables.map(v => `<div class="var-popup-item" data-value="${v.value}">${v.label}</div>`).join('')}
                `;
                        div.querySelectorAll('.var-popup-item').forEach(item => {
                            item.addEventListener('click', (e) => {
                                const value = e.currentTarget?.dataset?.value || e.target?.dataset?.value || '';
                                const beforeValue = String(editor?.value || '');

                                try {
                                    editor?.s?.focus?.();
                                } catch { }

                                try {
                                    editor.selection.insertHTML(value);
                                } catch { }

                                const afterValue = String(editor?.value || '');
                                if (afterValue === beforeValue) {
                                    editor.value = `${beforeValue}${value}`;
                                }

                                setTemplateContent(String(editor?.value || ''));
                                close();
                            });
                        });
                        return div;
                    }
                },
                ...(isAnalyse ? [{
                    name: 'tests',
                    tooltip: 'Insert Biological Analyses',
                    popup: (editor, current, self, close) => {
                        const div = editor.create.div();
                        const analysisToken = '[TESTS]';

                        div.innerHTML = `
                    <style>
                        .tests-popup-item {
                            padding: 8px 12px;
                            cursor: pointer;
                            white-space: nowrap;
                        }
                        .tests-popup-item:hover {
                            background-color: #f0f0f0;
                        }
                    </style>
                    <div class="tests-popup-item" data-value="${analysisToken}">Insert [TESTS] Variable</div>
                `;
                        div.querySelectorAll('.tests-popup-item').forEach(item => {
                            item.addEventListener('click', (e) => {
                                const value = e.currentTarget?.dataset?.value || e.target?.dataset?.value || '';
                                const beforeValue = String(editor?.value || '');

                                try {
                                    editor?.s?.focus?.();
                                } catch { }

                                try {
                                    editor.selection.insertHTML(value);
                                } catch { }

                                const afterValue = String(editor?.value || '');
                                if (afterValue === beforeValue) {
                                    editor.value = `${beforeValue}${value}`;
                                }

                                setTemplateContent(String(editor?.value || ''));
                                close();
                            });
                        });
                        return div;
                    }
                }] : [])
            ],
            height: 400
        };
    }, [currentCategory?.type, allPatientDetails, userData]);

    useEffect(() => {
        fetchTemplates();
    }, [refreshKey]);

    const fetchTemplates = async () => {
        try {
            setIsLoading(true);
            const response = await fetchPrescriptionSpecialTemplatesApi();
            if (response && response.data.res === true) {
                setSpecialTemplates(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching special templates:", error);
            toast.error("Erreur lors du chargement des modèles");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredTemplates = useMemo(() => {
        const result = {};
        categories.forEach(cat => {
            result[cat.type] = specialTemplates
                .filter(t => t.type === cat.type)
                .sort((a, b) => a.display_order - b.display_order);
        });
        return result;
    }, [specialTemplates, categories]);

    const handleAddClick = (cat) => {
        setCurrentCategory(cat);
        setTemplateName("");
        setTemplateContent("");
        setSelectedTemplateId(null);
        setShowEditorModal(true);
    };

    const handleEditClick = (template, cat) => {
        setCurrentCategory(cat);
        setSelectedTemplateId(template.id);
        setTemplateName(template.title);
        setTemplateContent(template.content);
        setShowEditorModal(true);
    };

    const handleSave = async () => {
        if (!templateName.trim()) {
            toast.warning("Please enter a name.", { autoClose: 1500 });
            return;
        }

        try {
            setIsSaving(true);
            let response;

            if (selectedTemplateId) {
                // Update
                const data = {
                    id: selectedTemplateId,
                    title: templateName.trim(),
                    content: templateContent
                };
                response = await updatePrescriptionSpecialTemplateApi(data);
            } else {
                // Add
                const data = {
                    title: templateName.trim(),
                    type: currentCategory.type,
                    content: templateContent
                };
                response = await addPrescriptionSpecialTemplateApi(data);
            }

            if (response && response.data.res === true) {
                queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
                toast.success(selectedTemplateId ? "Updated successfully" : "Added successfully", { autoClose: 1500 });
                setShowEditorModal(false);
                fetchTemplates();
            } else {
                toast.error(response?.data?.msg || "An error occurred", { autoClose: 1500 });
            }
        } catch (error) {
            console.error(error);
            toast.error("Erreur serveur", { autoClose: 1500 });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this template?")) {
            try {
                const response = await deletePrescriptionSpecialTemplateApi({ id });
                if (response && response.data.res === true) {
                    queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
                    toast.success("Deleted successfully", { autoClose: 1500 });
                    fetchTemplates();
                }
            } catch (error) {
                console.error(error);
                toast.error("Error deleting template", { autoClose: 1500 });
            }
        }
    };

    // Drag and Drop Logic
    const [draggedItem, setDraggedItem] = useState(null);

    const onDragStart = (e, index, type) => {
        setDraggedItem({ index, type });
        e.dataTransfer.effectAllowed = "move";
    };

    const onDragOver = (e) => {
        e.preventDefault();
    };

    const onDrop = async (e, dropIndex, type) => {
        e.preventDefault();
        if (!draggedItem || draggedItem.type !== type) return;

        const items = [...filteredTemplates[type]];
        const draggedIdx = draggedItem.index;

        // Move the item
        const [movedItem] = items.splice(draggedIdx, 1);
        items.splice(dropIndex, 0, movedItem);

        // Update local state for immediate feedback
        const updatedTemplates = specialTemplates.map(t => {
            if (t.type !== type) return t;
            const newIndex = items.findIndex(item => item.id === t.id);
            return { ...t, display_order: newIndex };
        });
        setSpecialTemplates(updatedTemplates);

        // Call API to save new order
        try {
            const orders = items.map((item, idx) => ({
                id: item.id,
                display_order: idx
            }));
            await updatePrescriptionSpecialTemplateOrderApi({ orders });
            queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.specialTemplates() });
        } catch (error) {
            console.error("Error updating order:", error);
            toast.error("Erreur lors de la mise à jour de l'ordre");
        }

        setDraggedItem(null);
    };

    return (
        <div className="special_templates_management" style={{ margin: '0px' }}>
            <div className="cmn_prescription_management_box" style={{ margin: '20px 0', display: 'flex', justifyContent: 'space-between' }}>
                <h3>Text Templates</h3>
                <button
                    onClick={() => onOpenDownloadModal?.()}
                    style={{
                        width: '200px',
                        height: '53px',
                        backgroundColor: '#687eff',
                        borderRadius: '50px',
                        fontFamily: '"Rubik", sans-serif',
                        fontSize: '16px',
                        fontWeight: '400',
                        color: '#fff',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4861f4'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#687eff'}
                >
                    Download templates
                </button>
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                {categories.map((cat) => {
                    const templates = filteredTemplates[cat.type] || [];

                    return (
                        <div
                            key={cat.id}
                            className="special-template-card"
                            style={{
                                flex: '1',
                                minWidth: '220px',
                                border: '1px solid #eaeef1',
                                borderRadius: '8px',
                                padding: '15px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                backgroundColor: '#fff',
                                minHeight: '350px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}
                        >
                            <div style={{
                                width: '100%',
                                textAlign: 'center',
                                marginBottom: '15px',
                                borderBottom: `1px solid #eaeef1`,
                                paddingBottom: '10px'
                            }}>
                                <h4 style={{ fontSize: '13px', fontWeight: '600', margin: 0, color: '#000', textTransform: 'uppercase' }}>
                                    {cat.title}
                                </h4>
                            </div>

                            <div style={{ width: '100%', flex: 1, overflowY: 'auto', marginBottom: '15px' }}>
                                {isLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>Chargement...</div>
                                ) : templates.length > 0 ? (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        {templates.map((t, idx) => (
                                            <li
                                                key={t.id}
                                                draggable
                                                onDragStart={(e) => onDragStart(e, idx, cat.type)}
                                                onDragOver={onDragOver}
                                                onDrop={(e) => onDrop(e, idx, cat.type)}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f4ff'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                                style={{
                                                    padding: '8px 12px',
                                                    borderBottom: '1px solid #f0f2ff',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    cursor: 'grab',
                                                    backgroundColor: '#fff',
                                                    transition: 'background-color 0.2s ease',
                                                    borderRadius: '4px',
                                                    marginBottom: '2px'
                                                }}
                                            >
                                                <span style={{ fontSize: '12px', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                                                    {t.title}
                                                </span>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <button onClick={() => handleEditClick(t, cat)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                                        <img src={editIcon} alt="edit" style={{ width: '14px' }} />
                                                    </button>
                                                    <button onClick={() => handleDelete(t.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                                                        <img src={deleteIcon} alt="delete" style={{ width: '14px' }} />
                                                    </button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div style={{ textAlign: 'center', color: '#888', fontSize: '13px', padding: '20px 10px', border: '1px dashed #eaeef1', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                                        Aucun modèle pas encore
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop: 'auto' }}>
                                <button
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '45px', height: '45px', borderRadius: '50%', backgroundColor: '#f0f2ff'
                                    }}
                                    onClick={() => handleAddClick(cat)}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#687eff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {showEditorModal && (
                <div className="modal TemplateModal" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}>
                    <div className="modal-content" style={{ width: '90%', maxWidth: '800px', backgroundColor: '#fff', borderRadius: '12px', padding: 0, overflow: 'hidden' }}>
                        <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>{selectedTemplateId ? 'Edit' : 'Add'} Template</h2>
                            <button onClick={() => setShowEditorModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px' }}>
                                <img src={cancelIcon} alt="close" style={{ width: '24px' }} />
                            </button>
                        </div>
                        <div className="modal-body" style={{ padding: '30px' }}>
                            <div style={{ marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <label style={{ fontSize: '16px', fontWeight: '500', color: '#333', whiteSpace: 'nowrap' }}>Template name:</label>
                                <input
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder="Enter name..."
                                    style={{ flex: 1, padding: '10px 15px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', outline: 'none' }}
                                />
                            </div>
                            <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                                <JoditEditor
                                    key={`${selectedTemplateId || 'new-template'}-${showEditorModal ? 'open' : 'closed'}`}
                                    value={templateContent}
                                    config={joditConfig}
                                    onBlur={newContent => setTemplateContent(newContent)}
                                />
                            </div>
                        </div>
                        <div className="modal-footer" style={{ padding: '20px 30px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={{
                                    padding: '12px 45px',
                                    borderRadius: '30px',
                                    border: 'none',
                                    backgroundColor: '#687eff',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                    boxShadow: '0 4px 10px rgba(104, 126, 255, 0.3)'
                                }}
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpecialTemplatesManagement;
